import { Grid, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';

import Input from '../Input';

import type { PersonNameChildAttribute, PersonNameValue } from './types';
import { usePersonName } from './usePersonName';

export interface InputPersonNameProps {
  /** 氏名の値 */
  value: PersonNameValue;
  /** 値変更時のコールバック */
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
  /** 読み取り専用フラグ */
  readonly?: boolean;
  /** 無効化フラグ */
  disabled?: boolean;
}

/**
 * 氏名入力コンポーネント
 *
 * childAttributesプロパティで各フィールドのname/required/readonlyを個別に指定。
 * 表示順序は 姓→名→ミドルネーム の順で、名前・カナ・英語名の各行を表示。
 * childAttributesに含まれるフィールドのみが表示される。
 */
export const InputPersonName = ({
  value,
  onChange,
  childAttributes,
  readonly = false,
  disabled = false,
}: InputPersonNameProps) => {
  const {
    nameFields,
    kanaFields,
    enFields,
    isFieldReadonly,
    handleFieldChange,
  } = usePersonName({
    value,
    onChange,
    childAttributes,
    readonly,
  });

  /**
   * フィールドをレンダリングするヘルパー
   */
  const renderField = (child: PersonNameChildAttribute) => {
    const fieldReadonly = isFieldReadonly(child);

    return (
      <VStack key={child.attributeKey} className="gap-1">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {child.name}
        </Text>
        <Input
          value={value[child.fieldKey] ?? ''}
          onChange={(e) => handleFieldChange(child.fieldKey, e.target.value)}
          disabled={disabled || fieldReadonly}
          readOnly={fieldReadonly}
        />
      </VStack>
    );
  };

  const maxColumns = Math.max(
    nameFields.length,
    kanaFields.length,
    enFields.length,
  );

  /**
   * フィールドを行にレンダリングするヘルパー
   */
  const renderFieldRow = (fields: PersonNameChildAttribute[]) => {
    if (fields.length === 0) return null;
    return (
      <Grid columns={maxColumns} className="gap-4">
        {fields.map(renderField)}
      </Grid>
    );
  };

  // childAttributesが空の場合は何も表示しない
  if (
    nameFields.length === 0 &&
    kanaFields.length === 0 &&
    enFields.length === 0
  ) {
    return null;
  }

  return (
    <VStack className="gap-4">
      {renderFieldRow(nameFields)}
      {renderFieldRow(kanaFields)}
      {renderFieldRow(enFields)}
    </VStack>
  );
};
